import type { Animated } from 'react-native';

import {
    BLOCK_CODE,
    BLOCK_CODE_LINE,
    BLOCK_CONTENT_REF,
    BLOCK_DIVIDER,
    BLOCK_DRAWING,
    BLOCK_EMBED,
    BLOCK_EXPANDABLE,
    BLOCK_FILE,
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_HEADING_3,
    BLOCK_HINT,
    BLOCK_IMAGE,
    BLOCK_IMAGES,
    BLOCK_INTEGRATION,
    BLOCK_LIST_ITEM,
    BLOCK_MATH,
    BLOCK_OPENAPI,
    BLOCK_OPENAPI_OPERATION,
    BLOCK_OPENAPI_SCHEMAS,
    BLOCK_OPENAPI_WEBHOOK,
    BLOCK_PARAGRAPH,
    BLOCK_QUOTE,
    BLOCK_STEPPER,
    BLOCK_STEPPER_STEP,
    BLOCK_TABLE,
    BLOCK_TABS,
    BLOCK_UPDATES,
    type BlockTableData,
    type TableColumnDefinition,
    type TableRecordValue,
    table,
} from '@gitbook/doc-core';
import { spacing } from '@gitbook/react-uikit';
import { Block, type Document, type Fragment, type NodeType } from '@gitbook/slate';
import tokens from '@gitbook/spine-tokens';
import type { List } from '@gitbook/util-immutable';
import { assertNever } from '@gitbook/util-ts';

import type { ColumnWidthsMap } from '../../table';
import {
    BLOCKQUOTE_BORDER_WIDTH,
    BLOCKQUOTE_PADDING_LEFT,
    HINT_BORDER_WIDTH,
    HINT_PADDING_LEFT,
} from './constants';
import { getBlockSpacing } from './spacing';
import {
    computeColumnWidths,
    DEFAULT_TABLE_COLUMN_WIDTH,
    // TODO: LegacyColumnWidthsMap will be removed once we've migrated to the new column widths
    type ColumnWidthsMap as LegacyColumnWidthsMap,
} from './table';
/**
 * Estimate the height of a block.
 */
export function getBlockEstimatedHeight(
    node: Block,
    parent: Block | Document | Fragment,
    options?: {
        contentSize?: number;
        blockSpacing?: {
            paddingTop: number;
            paddingBottom: number;
        };
    }
): number {
    const { blockSpacing = { paddingTop: 0, paddingBottom: 0 }, contentSize = 750 } = options || {};

    if (node?.object !== 'block') {
        throw new Error(
            `invalid node passed to "getBlockEstimatedHeight" ${node.object}#${
                node.type
            } with parent ${node.object}#${parent ? parent.type : 'no-parent'}`
        );
    }

    switch (node.type) {
        case BLOCK_TABLE:
            return (
                blockSpacing.paddingTop +
                blockSpacing.paddingBottom +
                getEstimatedTableHeight(node, contentSize)
            );

        case BLOCK_TABS:
            return (
                blockSpacing.paddingTop +
                blockSpacing.paddingBottom +
                getBlockEstimatedHeight(node.nodes.first() as Block, node)
            );

        //
        // Leaf blocks
        //
        case BLOCK_PARAGRAPH:
        case BLOCK_HEADING_2:
        case BLOCK_HEADING_3:
        case BLOCK_HEADING_1: {
            const blockHeight =
                blockSpacing.paddingTop +
                blockSpacing.paddingBottom +
                getEstimatedTextHeight(node.text, {
                    lineHeight: getBlockLineHeight(node),
                    fontSize: getBlockFontSize(node),
                    contentWidth: contentSize,
                });

            if (
                node.type === BLOCK_HEADING_1 &&
                parent.object === 'document' &&
                parent.getChildIndex(node.key) !== 0
            ) {
                // HEADING_1, unless it's the first element in the document, has an
                // extra separator from other elements.
                return blockHeight + spacing.medium + spacing.large + 1;
            }

            return blockHeight;
        }
        case BLOCK_CODE_LINE:
            return getEstimatedTextHeight(node.text, {
                lineHeight: getBlockLineHeight(node),
                fontSize: getBlockFontSize(node),
                contentWidth: contentSize,
            });

        //
        // Void blocks
        //
        case BLOCK_IMAGES:
        case BLOCK_EMBED:
        case BLOCK_IMAGE:
        case BLOCK_OPENAPI:
        case BLOCK_OPENAPI_OPERATION:
        case BLOCK_OPENAPI_SCHEMAS:
        case BLOCK_OPENAPI_WEBHOOK:
        case BLOCK_DRAWING:
        case BLOCK_INTEGRATION:
            return blockSpacing.paddingTop + blockSpacing.paddingBottom + 200;
        case BLOCK_MATH:
        case BLOCK_FILE:
        case BLOCK_CONTENT_REF:
            return blockSpacing.paddingTop + blockSpacing.paddingBottom + 100;
        case BLOCK_EXPANDABLE:
            return blockSpacing.paddingTop + blockSpacing.paddingBottom + getBlockLineHeight(node);
        case BLOCK_DIVIDER:
            return blockSpacing.paddingTop + blockSpacing.paddingBottom + 1;

        //
        // Block with children
        //
        default: {
            const usableWidth = getUsableChildrenWidth(node, contentSize);

            return (
                blockSpacing.paddingTop +
                blockSpacing.paddingBottom +
                (node.nodes as List<Block>).reduce((previous, child) => {
                    const spacing = getBlockSpacing(child, node);

                    const childHeight =
                        child.object === 'block'
                            ? getBlockEstimatedHeight(child, node, { contentSize: usableWidth })
                            : getEstimatedTextHeight(child.text, { contentWidth: usableWidth });

                    return previous + childHeight + spacing.paddingTop + spacing.paddingBottom;
                }, 0)
            );
        }
    }
}

/**
 * Gets the available width for children of the given node. Some blocks,
 * such as quotes, have things like borders or indentation that reduces
 * the available width of their content and could affect the height calculation
 * (by having text wrap onto the next line).
 */
function getUsableChildrenWidth(node: Block, contentSize: number): number {
    switch (node.type) {
        case BLOCK_QUOTE:
            return contentSize - BLOCKQUOTE_PADDING_LEFT - BLOCKQUOTE_BORDER_WIDTH;
        case BLOCK_HINT:
            return contentSize - HINT_PADDING_LEFT - HINT_BORDER_WIDTH;
        default:
            return contentSize;
    }
}

/**
 * Get the line-height of the first line of text in a block.
 * The block controls button will be vertically centered with it.
 */
export function getBlockLineHeight(node: NodeType | null | undefined): number {
    if (!node || !Block.isBlock(node)) {
        return 24;
    }

    switch (node.type) {
        case BLOCK_LIST_ITEM: {
            const firstNode = node.nodes.first();
            // For list item, the line height is the same as the one from the first block in it.
            return getBlockLineHeight(firstNode);
        }
        case BLOCK_HEADING_1:
            return 32; // Wrong on max-md, also if default text size is changed.
        case BLOCK_HEADING_2:
            return 28; // Wrong on max-md, also if default text size is changed.
        case BLOCK_CODE:
            // For code block, we align with the inner code line by taking spacing in consideration
            return 22 + 2 * 8;
        case BLOCK_CODE_LINE:
            return 22;
        case BLOCK_TABS:
            // For tabs, we align with the title in the header
            return 38;
        case BLOCK_TABLE:
            // For table we align with the height of cells
            return 40;
        case BLOCK_EXPANDABLE:
            // For expandable we align with the title in the header
            return 58;
        case BLOCK_OPENAPI:
            // For OpenAPI blocks, we align with the title in the header
            return 28;
        // For quote, we align with the first line of text
        case BLOCK_QUOTE:
            return 32;
        // For hint, we align with the first line of text
        case BLOCK_HINT:
            return 56;
        case BLOCK_DIVIDER:
            return 5;
        // For stepper, we align on the number of the step
        case BLOCK_STEPPER:
        case BLOCK_STEPPER_STEP:
            return 28;
        case BLOCK_UPDATES:
            return 8;
        default:
            return 24;
    }
}

/**
 * Get the font-size used in a leaf block.
 */
export function getBlockFontSize(node: NodeType | null | undefined): number {
    const { typography } = tokens;

    if (!node || !Block.isBlock(node)) {
        return typography.content.paragraph['font-size'];
    }

    switch (node.type) {
        case BLOCK_LIST_ITEM:
            // For list item, the line height is the same as the one from the first block in it.
            return getBlockFontSize(node.nodes.first());
        case BLOCK_HEADING_1:
            return typography.content.heading.large['font-size'];
        case BLOCK_HEADING_2:
            return typography.content.heading.medium['font-size'];
        case BLOCK_HEADING_3:
            return typography.content.heading.small['font-size'];
        case BLOCK_CODE_LINE:
            return typography.content.mono['font-size'];
        default:
            return typography.content.paragraph['font-size'];
    }
}

/**
 * Calculate the estimated height of a table.
 */
export function getEstimatedTableHeight(node: Block, contentSize: number): number {
    const tableData = table.getTableData(node);

    switch (tableData.view.type) {
        case 'grid': {
            const { widths } = computeColumnWidths(tableData.view, contentSize);
            const headerHeight = getEstimatedHeaderHeight(tableData, widths);
            const rowsHeight = Object.keys(tableData.records).reduce((size, recordId) => {
                const rowHeight = getEstimatedTableRowHeight(node, recordId, widths);
                return size + rowHeight;
            }, 0);

            return headerHeight + rowsHeight;
        }
        case 'cards': {
            const cardsCount = Object.keys(tableData.records).length;
            const cardsPerRow = tableData.view.cardSize === 'large' ? 2 : 3;
            const cardWidth = (750 - cardsPerRow * spacing.medium) / cardsPerRow;
            const cardHeight = getEstimatedTableCardHeight(node, tableData, cardWidth);
            const cardsRowCount = Math.ceil(cardsCount / cardsPerRow);

            return cardsRowCount * cardHeight + (cardsRowCount - 1) * spacing.medium;
        }
        default:
            assertNever(tableData.view);
    }
}

/**
 * Calculate the estimated height of an individual row in the table.
 */
export function getEstimatedTableRowHeight(
    node: Block | undefined,
    recordId: string,
    // TODO: LegacyColumnWidthsMap will be removed once we've migrated to the new column widths
    widths: LegacyColumnWidthsMap | ColumnWidthsMap
): number {
    if (!node) {
        return 0;
    }

    const data = table.getTableData(node);
    const record = data.records[recordId];

    // Iterate over all columns and take the tallest one.
    return data.view.columns.reduce((max, columnId) => {
        const value = record.values[columnId];
        const definition = data.definition[columnId];
        const height = getEstimatedCellHeight(
            value,
            definition,
            getColumnWidth(definition.id, widths),
            node
        );

        return height > max ? height : max;
    }, spacing.medium);
}

/**
 * Calculate the estimated height of an individual card in the table.
 */
export function getEstimatedTableCardHeight(
    node: Block,
    data: BlockTableData,
    cardWidth: number
): number {
    const firstRecord = Object.values(data.records)[0];
    if (!firstRecord) {
        return 0;
    }

    const coverHeight =
        data.view.type === 'cards' && (data.view.coverDefinition || data.view.coverDefinitionDark)
            ? 135
            : 0;

    return data.view.columns.reduce(
        (sum, columnId) => {
            const value = firstRecord.values[columnId];
            const definition = data.definition[columnId];
            const height = getEstimatedCellHeight(value, definition, cardWidth, node);

            return sum + height;
        },
        2 * spacing.small + coverHeight
    );
}

/**
 * Estimate the height of the table header.
 */
function getEstimatedHeaderHeight(
    data: BlockTableData,
    widths: LegacyColumnWidthsMap | ColumnWidthsMap
): number {
    if (data.view.type !== 'grid' || data.view.hideHeader) {
        return 0;
    }

    return Object.keys(data.definition).reduce((max, columnId) => {
        const columnWidth = getColumnWidth(columnId, widths);
        const headerText = data.definition[columnId].title;
        const height = getEstimatedTextHeight(headerText, { contentWidth: columnWidth });

        return height > max ? height : max;
    }, spacing.medium);
}

/**
 * Gets the usable column width for the given columnId.
 */
function getColumnWidth(columnId: string, widths: LegacyColumnWidthsMap | ColumnWidthsMap): number {
    const value = widths.get(columnId);
    const width = isAnimatedWidthObject(value)
        ? value.width
        : typeof value === 'number'
          ? value
          : DEFAULT_TABLE_COLUMN_WIDTH;

    // cells have a horizontal padding of spacing.medium, which takes away from the available space
    return width - spacing.medium * 2;
}

/**
 * Estimate the height of an individual cell in the table.
 */
function getEstimatedCellHeight(
    value: TableRecordValue,
    definition: TableColumnDefinition,
    contentWidth: number,
    node: Block
): number {
    const height = (() => {
        switch (definition.type) {
            // With text we estimate the height of the blocks like normal
            case 'text': {
                const fragment = node.fragments.get(String(value));

                if (!fragment) {
                    return 0;
                }

                return fragment.nodes.reduce((sum, block) => {
                    return (
                        sum + getBlockEstimatedHeight(block, node, { contentSize: contentWidth })
                    );
                }, 0);
            }
            // Numbers are rendered as text with a fixed font size
            case 'number':
                return getEstimatedTextHeight(String(value), {
                    contentWidth,
                });
            // Files and users follow the same principle: take an average tab size, multiply it by the number
            // of users/files and find how many lines there might be.
            case 'files':
            case 'users': {
                const count = Array.isArray(value) ? value.length : 1;
                const estimatedWidth =
                    definition.type === 'files'
                        ? ESTIMATED_FILE_COLUMN_WIDTH
                        : ESTIMATED_USER_AVATAR_WIDTH;

                const lines =
                    count <= 1
                        ? 1
                        : Math.max(1, Math.ceil((estimatedWidth * count) / contentWidth));

                return lines * 24;
            }
            default:
                return 24;
        }
    })();

    // Each cell has medium padding both top and bottom, plus a bottom border of 1px
    return height + spacing.medium * 2 + 1;
}

type TextMeasurementOptions = {
    lineHeight?: number;
    fontSize?: number;
    contentWidth?: number;
};

/**
 * Estimate the height of text depending on its length and its container width.
 */
function getEstimatedTextHeight(text: string, options?: TextMeasurementOptions): number {
    const { lineHeight = 24, contentWidth = 750 } = options ?? {};
    const textWidth = getEstimatedTextWidth(text, options);

    // We always have at least one line (empty text is replaced by a placeholder)
    const lines = Math.max(1, Math.ceil(textWidth / contentWidth));

    return lineHeight * lines;
}

/**
 * Estimate the width of text based on the given font.
 */
function getEstimatedTextWidth(text = '', options?: TextMeasurementOptions): number {
    const { fontSize = 16 } = options || {};
    const fontWidthHeightRatio = 0.5;
    const charWidth = fontSize * fontWidthHeightRatio;

    return charWidth * text.length;
}

/**
 * Estimated width of a user avatar - takes a typical name and adds sizing for the icon and any margins.
 */
const ESTIMATED_USER_AVATAR_WIDTH =
    getEstimatedTextWidth('Steven Smithson') + spacing.large + spacing.small * 2;

/** Estimated width of a file name. */
const ESTIMATED_FILE_COLUMN_WIDTH =
    getEstimatedTextWidth('this_is_a_file_of_mine.json') + spacing.large + spacing.small * 2;

/**
 * Temporary helper to check if the value is a legacy Animated.Value RN object or a plain number.
 */
function isAnimatedWidthObject(
    value:
        | number
        | { controller: Animated.Value; animated: Animated.Value; width: number }
        | undefined
): value is { controller: Animated.Value; animated: Animated.Value; width: number } {
    return typeof value === 'object' && value !== null && 'width' in value;
}
